import React, { useState } from "react";
import { useDispatch } from "react-redux";
import SubmitButton from "../../../components/button/SubmitButton";
import endpoints from "../../../apis/endpoints";
import { startOscillaLoadingValues } from "../../../store/actions/OscillaDataAction";
import { callSnackBar } from "../../../store/actions/snackbarAction";
import { SNACK_BAR_VARIETNS } from "../../../utils/constants";

/**
 * Try to open custom protocol and decide if the app is installed.
 * Resolves "opened" only when we observe a focus/visibility change event
 * that strongly indicates the user left the browser (likely the native app opened).
 *
 * If no such event occurs within timeoutMs, resolves "not-installed".
 *
 * NOTE: This cannot be 100% reliable because browsers intentionally limit what they expose.
 */
function openWithDetection(protocolUrl, timeoutMs = 15000) {
  return new Promise((resolve) => {
    let handled = false;
    let iframe = null;
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("pagehide", onPageHide);
      if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    };

    const onSuccess = (reason) => {
      if (handled) return;
      handled = true;
      cleanup();
      // console.log("openWithDetection: success:", reason);
      resolve("opened");
    };

    const onFailure = (reason) => {
      if (handled) return;
      handled = true;
      cleanup();
      // console.log("openWithDetection: failure:", reason);
      resolve("not-installed");
    };

    const onVisibilityChange = () => {
      if (document.hidden) onSuccess("visibilitychange:document.hidden");
    };
    const onBlur = () => onSuccess("window.blur");
    const onPageHide = () => onSuccess("pagehide");

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onBlur);
    window.addEventListener("pagehide", onPageHide);

    // Try to open using location (most consistent when triggered by user click)
    try {
      window.location.href = protocolUrl;
    } catch (err) {
      // ignore
      // console.warn("openWithDetection: window.location failed", err);
    }

    // Also try an iframe fallback (some browsers respond to it)
    try {
      iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = protocolUrl;
      // append won't change the page in most cases; it's just an attempt
      document.body.appendChild(iframe);
    } catch (err) {
      // console.warn("openWithDetection: iframe attempt failed", err);
    }

    // After timeoutMs, if we haven't seen any event -> assume not installed
    timeoutId = setTimeout(() => {
      onFailure("timeout");
    }, timeoutMs);
  });
}

export default function OscillaLauncher({
  wehearId,
  patientId,
  firstName,
  lastName,
  org,
  birthDate,
  gender,
  setButton,
}) {
  const dispatch = useDispatch();
  const [checking, setChecking] = useState(false);

  const launch = async (e) => {
    e.preventDefault();
    if (checking) return;

    setChecking(true);
    if (typeof setButton === "function") setButton(true);
    dispatch(startOscillaLoadingValues(true)); // uncomment if you want global loading

    try {
      const path = `${endpoints.oscillaResult}?patient_id=${patientId}`;
      const oscillaGender = gender?.startsWith("M") ? "M" : "F";
      const oscillaBirthDate = birthDate?.includes("-")
        ? birthDate.replaceAll("-", "")
        : birthDate;

      const enc = (val) => encodeURIComponent(val || "");
      const dataEncryption = 0;
      const headerName = "";
      const headerVal = "";
      const output = 35;
      const cprId = wehearId;

      const protocolUrl =
        `audioconsole:webapi2|${dataEncryption}|${path}|${headerName}|${headerVal}|${output}` +
        `|${enc(patientId)}` +
        `|${enc(cprId)}` +
        `|${enc(firstName)}` +
        `|${enc(lastName)}` +
        `|${enc(org)}` +
        `|${oscillaBirthDate}` +
        `|${oscillaGender}` +
        `||`; // operator/provider empty

      const downloadPage = "https://www.oscilla.dk/download/ac_setup_450.exe";

      const result = await openWithDetection(protocolUrl, 15000);

      if (result === "opened") {
        console.log("Opened the Oscilla app successfully.");
        // you can proceed with any post-open logic here
      } else {
        console.log(
          "Oscilla app not installed — redirecting to download page.",
        );
        // redirect user to download or show UI
        if (downloadPage) {
          // small delay so log/UI updates are visible
          setTimeout(() => {
            dispatch(startOscillaLoadingValues(false));
            dispatch(
              callSnackBar(
                "Oscilla app not installed.",
                SNACK_BAR_VARIETNS.warning,
              ),
            );
            // window.location.href = downloadPage;
          }, 200);
        }
      }
    } catch (err) {
      console.error("Error while launching Oscilla:", err);
    } finally {
      setChecking(false);
      if (typeof setButton === "function") setButton(false);
      // dispatch(startOscillaLoadingValues(false)); // uncomment if using global loading
    }
  };

  return (
    <SubmitButton
      variants="outlined"
      type="button"
      onClick={launch}
      sx={{ minWidth: "200px" }}
      title={checking ? "Checking..." : "Start Oscilla Test"}
      disabled={checking}
    />
  );
}
